import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import {
  Download,
  Share,
  Copy,
  CheckCircle,
  XCircle,
  Warning,
  Question,
  ShieldCheck,
  Flag,
  Target,
  Lightning,
  Books,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { isFactCheckerSource } from "@/lib/factCheckers";

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------
const TECHNIQUE_LABELS = {
  name_calling: "Name-calling",
  guilt_by_association: "Guilt by association",
  casting_doubt: "Casting doubt",
  appeal_to_hypocrisy: "Appeal to hypocrisy",
  questioning_reputation: "Questioning reputation",
  flag_waving: "Flag-waving",
  appeal_to_authority: "Appeal to authority",
  appeal_to_popularity: "Appeal to popularity",
  red_herring: "Red herring",
  straw_man: "Straw man",
  whataboutism: "Whataboutism",
  black_and_white_fallacy: "Black-and-white fallacy",
  causal_oversimplification: "Causal oversimplification",
  thought_terminating_cliche: "Thought-terminating cliché",
  slogans: "Slogans",
  repetition: "Repetition",
  conversation_killer: "Conversation killer",
  loaded_language: "Loaded language",
  appeal_to_fear: "Appeal to fear",
  exaggeration_minimisation: "Exaggeration",
  obfuscation: "Obfuscation",
  cherry_picking: "Cherry-picking",
  false_dilemma: "False dilemma",
};

const FRAME_LABELS = {
  anti_establishment: "Anti-establishment",
  health_conspiracy: "Health conspiracy",
  political_polarization: "Political polarization",
  us_vs_them: "Us vs. them",
  nationalist: "Nationalist",
  apocalyptic: "Apocalyptic",
  victimhood: "Victimhood",
  techno_utopian: "Techno-utopian",
  populist: "Populist",
  none: "Neutral framing",
};

const INTENT_LABELS = {
  inform: "Inform",
  persuade: "Persuade",
  manipulate: "Manipulate",
  misinform: "Misinform",
  sell: "Sell",
  polarize: "Polarize",
  radicalize: "Radicalize",
  satire: "Satire",
  unclear: "Unclear",
};

const VERDICT_LABELS = {
  TRUE: "True",
  FALSE: "False",
  PARTIALLY_TRUE: "Partial",
  UNVERIFIABLE: "Unverifiable",
  OUTDATED: "Outdated",
  SATIRE: "Satire",
};

// ---------------------------------------------------------------------------
// SVG arc gauge — 240° sweep opening at the bottom, filled to `value` (0-100)
// ---------------------------------------------------------------------------
function ArcGauge({ value, colorClass }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const size = 200;
  const stroke = 18;
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - stroke) / 2;
  const sweepDeg = 240;
  // In SVG angles: 0°=right(3o'clock), 90°=down(6o'clock), 180°=left(9), 270°=up(12).
  // We want a 240° sweep that OPENS at the bottom → start at 150° (lower-left)
  // and sweep clockwise through 180°/270°/0° to end at 30° (lower-right).
  const startAngle = 150;
  const filledEnd = startAngle + (sweepDeg * v) / 100;
  const endAngle = startAngle + sweepDeg;

  const polar = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const arcPath = (start, end) => {
    const [x1, y1] = polar(start);
    const [x2, y2] = polar(end);
    const large = end - start > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  // Height truncated to fit the arc without wasted space below
  const viewH = size * 0.78;

  return (
    <svg width={size} height={viewH} viewBox={`0 0 ${size} ${viewH}`} className="block">
      <path
        d={arcPath(startAngle, endAngle)}
        stroke="rgba(255,255,255,0.10)"
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
      />
      {v > 0 && (
        <motion.path
          d={arcPath(startAngle, filledEnd)}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          className={colorClass}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Score bar — 10-cell block, dark or light variant
// ---------------------------------------------------------------------------
function ScoreBar({ label, value, tone, testId, right }) {
  const clamped = Math.max(0, Math.min(100, value || 0));
  const filled = Math.round(clamped / 10);
  const toneClass = {
    accuracy: "bg-sky-600",
    manip: "bg-red-600",
    trust: "bg-emerald-600",
  }[tone] || "bg-zinc-800";
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider">
          {label}
        </div>
        <div className="flex items-baseline gap-2">
          {right && (
            <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider">
              {right}
            </div>
          )}
          <div className="font-mono-plex text-lg font-bold text-zinc-950 tabular-nums">
            {clamped}
            <span className="text-[10px] text-zinc-500 ml-0.5">%</span>
          </div>
        </div>
      </div>
      <div className="flex gap-1 h-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 border border-black ${i < filled ? toneClass : "bg-white"}`}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function VerdictReportCard({
  propaganda,
  factualAccuracy = 0,
  trustScore = 0,
  claims = [],
  overallVerdict = "UNVERIFIABLE",
  overallConfidence = 0,
  videoMetadata,
  cacheKey,
}) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // ---- derive source stats ------------------------------------------------
  const sourceStats = useMemo(() => {
    const seen = new Set();
    let total = 0;
    let tier12 = 0;
    let factChecker = 0;
    for (const c of claims) {
      for (const s of c.sources || []) {
        const key = (s.url || s.domain || "").toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        total++;
        if ((s.tier || 99) <= 2) tier12++;
        if (isFactCheckerSource(s)) factChecker++;
      }
    }
    return { total, tier12, factChecker };
  }, [claims]);

  // ---- claims breakdown ---------------------------------------------------
  const breakdown = useMemo(() => {
    const t = { TRUE: 0, FALSE: 0, PARTIALLY_TRUE: 0, UNVERIFIABLE: 0 };
    for (const c of claims) {
      if (t[c.verdict] != null) t[c.verdict]++;
    }
    return t;
  }, [claims]);

  const techniques = (propaganda?.techniques || []).slice(0, 7);
  const manipulation = propaganda?.manipulation_score || 0;
  const frame = propaganda?.narrative_frame || "none";
  const intent = propaganda?.intent || "unclear";

  // ---- trust colors -------------------------------------------------------
  const trustBand =
    trustScore >= 70 ? "high"
    : trustScore >= 40 ? "mid"
    : "low";
  const trustLabel = trustBand === "high" ? "High Trust" : trustBand === "mid" ? "Mixed Signals" : "Low Trust";
  const trustLevel = trustBand === "high" ? "Reliable" : trustBand === "mid" ? "Caution" : trustBand === "low" ? "Critical" : "Unknown";
  const arcColor = trustBand === "high" ? "stroke-emerald-400" : trustBand === "mid" ? "stroke-amber-400" : "stroke-red-500";
  const trustText = trustBand === "high" ? "text-emerald-400" : trustBand === "mid" ? "text-amber-400" : "text-red-500";

  // ---- action handlers ----------------------------------------------------
  const shareUrl = cacheKey ? `${window.location.origin}/check/${cacheKey}` : window.location.href;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleShare = async () => {
    const title = videoMetadata?.title ? `TrueStream verdict: ${videoMetadata.title}` : "TrueStream verdict";
    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
      } catch {
        // user cancelled — no-op
      }
    } else {
      handleCopy();
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      const slug = (videoMetadata?.title || "verdict")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40);
      link.download = `truestream-${slug || "verdict"}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Report card saved");
    } catch (e) {
      toast.error("Could not export image");
    } finally {
      setBusy(false);
    }
  };

  const verdictIcon = {
    TRUE: <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />,
    FALSE: <XCircle weight="fill" className="w-4 h-4 text-red-400" />,
    PARTIALLY_TRUE: <Warning weight="fill" className="w-4 h-4 text-amber-400" />,
    UNVERIFIABLE: <Question weight="fill" className="w-4 h-4 text-zinc-400" />,
  }[overallVerdict] || <Question weight="fill" className="w-4 h-4 text-zinc-400" />;

  return (
    <div data-testid="verdict-report-card">
      <div
        ref={cardRef}
        className="border-2 border-black bg-white shadow-[6px_6px_0px_rgba(0,0,0,1)] overflow-hidden"
      >
        {/* ---------------- HERO (dark) ---------------- */}
        <div
          data-testid="report-hero"
          className="bg-zinc-950 text-white px-6 py-6 md:px-8 md:py-8 relative"
        >
          <div className="flex items-start justify-between mb-2">
            <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-widest">
              TRUESTREAM VERDICT
            </div>
            <div className="flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-widest">
              {verdictIcon}
              <span className="text-zinc-300">
                {VERDICT_LABELS[overallVerdict] || overallVerdict}
                {overallConfidence > 0 && ` · ${Math.round(overallConfidence * 100)}%`}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 md:gap-8 items-center">
            {/* Gauge */}
            <div className="relative flex flex-col items-center">
              <div className="relative">
                <ArcGauge value={trustScore} colorClass={arcColor} />
                <div className="absolute inset-0 flex flex-col items-center justify-center pt-2">
                  <div className={`font-display text-5xl font-bold tabular-nums leading-none ${trustText}`}>
                    {Math.round(trustScore)}
                  </div>
                  <div className="font-mono-plex text-[9px] font-semibold text-zinc-500 tracking-widest mt-1">
                    TRUST SCORE
                  </div>
                </div>
              </div>
            </div>

            {/* Right side headline */}
            <div>
              <h3 className={`font-display text-3xl md:text-4xl leading-tight ${trustText}`}>
                {trustLabel}
              </h3>
              <div className="font-mono-plex text-[11px] text-zinc-400 tracking-wider mt-1">
                Level:&nbsp;
                <span className="text-zinc-200 font-semibold">{trustLevel}</span>
              </div>

              {/* Quick facts row */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white leading-none tabular-nums">
                    {claims.length}
                  </div>
                  <div className="font-mono-plex text-[9px] text-zinc-500 tracking-widest mt-1.5 uppercase">
                    Claims
                  </div>
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white leading-none tabular-nums">
                    {sourceStats.total}
                  </div>
                  <div className="font-mono-plex text-[9px] text-zinc-500 tracking-widest mt-1.5 uppercase">
                    Sources
                  </div>
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl text-white leading-none tabular-nums">
                    {techniques.length}
                  </div>
                  <div className="font-mono-plex text-[9px] text-zinc-500 tracking-widest mt-1.5 uppercase">
                    Techniques
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------- LIGHT BODY ---------------- */}
        {/* Score bars */}
        <div className="border-t-2 border-black px-6 py-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <ScoreBar
            label="FACTUAL ACCURACY"
            value={factualAccuracy}
            tone="accuracy"
            testId="report-factual-accuracy"
            right={`${breakdown.TRUE + Math.round(breakdown.PARTIALLY_TRUE / 2)} / ${claims.length || 0} sound`}
          />
          <ScoreBar
            label="MANIPULATION LEVEL"
            value={manipulation}
            tone="manip"
            testId="report-manipulation"
            right={manipulation >= 60 ? "High" : manipulation >= 30 ? "Moderate" : "Low"}
          />
        </div>

        {/* Narrative frame & intent */}
        <div className="border-t border-zinc-200 px-6 py-4 md:px-8 flex flex-wrap items-center gap-4 bg-zinc-50">
          <div className="flex items-center gap-2">
            <Flag weight="duotone" className="w-4 h-4 text-zinc-700" />
            <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider">
              FRAME
            </span>
            <span className="font-mono-plex text-[11px] font-semibold text-zinc-950 uppercase tracking-wide">
              {FRAME_LABELS[frame] || frame}
            </span>
          </div>
          <div className="h-4 w-px bg-zinc-300" />
          <div className="flex items-center gap-2">
            <Target weight="duotone" className="w-4 h-4 text-zinc-700" />
            <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider">
              INTENT
            </span>
            <span className="font-mono-plex text-[11px] font-semibold text-zinc-950 uppercase tracking-wide">
              {INTENT_LABELS[intent] || intent}
            </span>
          </div>
        </div>

        {/* Plain-English summary + recommendation */}
        {(propaganda?.summary || propaganda?.recommendation) && (
          <div
            data-testid="report-summary"
            className="border-t border-zinc-200 px-6 py-5 md:px-8"
          >
            {propaganda.summary && (
              <>
                <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider mb-2">
                  WHAT THIS MEANS
                </div>
                <p className="text-[14px] text-zinc-800 leading-relaxed">
                  {propaganda.summary}
                </p>
              </>
            )}
            {propaganda.recommendation && (
              <div className={`${propaganda.summary ? "mt-3 pt-3 border-t border-zinc-200" : ""}`}>
                <div className="font-mono-plex text-[10px] font-semibold text-emerald-700 tracking-wider mb-1">
                  RECOMMENDED ACTION
                </div>
                <p className="text-[13px] text-zinc-800 leading-snug italic">
                  {propaganda.recommendation}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Sources */}
        <div className="border-t border-zinc-200 px-6 py-5 md:px-8" data-testid="report-sources">
          <div className="flex items-center gap-2 mb-3">
            <Books weight="duotone" className="w-4 h-4 text-zinc-700" />
            <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider">
              SOURCES CITED
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="font-display text-2xl text-zinc-950 tabular-nums">{sourceStats.total}</div>
              <div className="font-mono-plex text-[9px] text-zinc-500 uppercase tracking-widest">Total</div>
            </div>
            <div>
              <div className="font-display text-2xl text-sky-700 tabular-nums">{sourceStats.tier12}</div>
              <div className="font-mono-plex text-[9px] text-zinc-500 uppercase tracking-widest">Tier 1/2</div>
            </div>
            <div>
              <div className="font-display text-2xl text-emerald-700 tabular-nums">{sourceStats.factChecker}</div>
              <div className="font-mono-plex text-[9px] text-zinc-500 uppercase tracking-widest">Fact-checker</div>
            </div>
          </div>
        </div>

        {/* Techniques ranked */}
        {techniques.length > 0 && (
          <div className="border-t border-zinc-200 px-6 py-5 md:px-8" data-testid="report-techniques">
            <div className="flex items-center gap-2 mb-3">
              <Lightning weight="duotone" className="w-4 h-4 text-red-600" />
              <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider">
                TECHNIQUES DETECTED · {techniques.length}
              </span>
            </div>
            <ol className="space-y-1.5">
              {techniques.map((t, i) => {
                const conf = Math.round((t.confidence || 0) * 100);
                const intense = conf >= 70;
                return (
                  <li
                    key={`${t.type}-${i}`}
                    className="grid grid-cols-[24px_16px_1fr_auto] items-center gap-3 py-1"
                  >
                    <span className="font-mono-plex text-[11px] text-zinc-400 tabular-nums">
                      {i + 1}
                    </span>
                    <span
                      className={`w-2.5 h-2.5 rounded-full border border-black ${
                        intense ? "bg-red-500" : "bg-amber-400"
                      }`}
                    />
                    <span className="text-[13px] font-semibold text-zinc-950">
                      {TECHNIQUE_LABELS[t.type] || t.type}
                    </span>
                    <span className="font-mono-plex text-[10px] text-zinc-500 tabular-nums">
                      {conf}%
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* Claims breakdown */}
        {claims.length > 0 && (
          <div className="border-t border-zinc-200 px-6 py-4 md:px-8 flex flex-wrap items-center gap-2" data-testid="report-claims-breakdown">
            <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider mr-1">
              CLAIMS
            </span>
            <Pill icon={<CheckCircle weight="fill" className="w-3.5 h-3.5" />} tone="emerald" label={`${breakdown.TRUE} True`} />
            <Pill icon={<XCircle weight="fill" className="w-3.5 h-3.5" />} tone="red" label={`${breakdown.FALSE} False`} />
            <Pill icon={<Warning weight="fill" className="w-3.5 h-3.5" />} tone="amber" label={`${breakdown.PARTIALLY_TRUE} Partial`} />
            <Pill icon={<Question weight="fill" className="w-3.5 h-3.5" />} tone="zinc" label={`${breakdown.UNVERIFIABLE} Unverifiable`} />
          </div>
        )}

        {/* Footer branding — visible in exported image */}
        <div className="border-t-2 border-black bg-zinc-100 px-6 py-3 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck weight="fill" className="w-4 h-4 text-emerald-700" />
            <span className="font-display text-sm font-bold text-zinc-950">
              True<span className="text-emerald-700">Stream</span>
            </span>
          </div>
          <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 tracking-wider">
            truestream.app
          </span>
        </div>
      </div>

      {/* Action row — OUTSIDE the exported card */}
      <div className="mt-4 flex flex-wrap gap-2">
        <ActionButton onClick={handleCopy} icon={<Copy weight="bold" />} label="COPY LINK" testId="report-copy" />
        <ActionButton onClick={handleShare} icon={<Share weight="bold" />} label="SHARE" testId="report-share" />
        <ActionButton
          onClick={handleDownload}
          icon={<Download weight="bold" />}
          label={busy ? "SAVING…" : "DOWNLOAD IMAGE"}
          testId="report-download"
          disabled={busy}
          primary
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function Pill({ icon, tone, label }) {
  const toneClass = {
    emerald: "border-emerald-700 bg-emerald-50 text-emerald-900",
    red: "border-red-700 bg-red-50 text-red-900",
    amber: "border-amber-700 bg-amber-50 text-amber-900",
    zinc: "border-zinc-500 bg-zinc-100 text-zinc-800",
  }[tone] || "border-zinc-500 bg-zinc-100 text-zinc-800";
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono-plex text-[11px] font-semibold ${toneClass}`}>
      {icon}
      {label}
    </span>
  );
}

function ActionButton({ onClick, icon, label, testId, primary, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-2 border-2 border-black px-4 py-2 font-mono-plex text-[11px] font-bold tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
        primary
          ? "bg-zinc-950 text-white hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[3px_3px_0px_rgba(0,0,0,1)]"
          : "bg-white text-zinc-950 hover:bg-zinc-100"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
